/**
 * SEO Utilities
 * Comprehensive SEO utilities for meta tags, Open Graph, Twitter Cards, and structured data
 * Created: 2026-02-27
 */

// Site configuration
const SITE_CONFIG = {
  name: 'GamesNest',
  url: process.env.VUE_APP_SITE_URL || 'https://gamesnest.org',
  description: 'Your ultimate destination for gaming news, reviews, guides, and deals',
  logo: '/logo.png',
  twitter: '@gamesnest',
  locale: 'en_US',
  type: 'website',
}

/**
 * Generate canonical URL with trailing slash
 * @param {string} path - URL path
 * @returns {string} Full canonical URL with trailing slash
 */
export function generateCanonicalUrl(path) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const [pathWithoutHash] = normalizedPath.split('#')
  const [pathname, query = ''] = pathWithoutHash.split('?')
  const pathWithSlash = pathname === '/' || pathname.endsWith('/')
    ? pathname
    : `${pathname}/`
  const querySuffix = query ? `?${query}` : ''
  return `${SITE_CONFIG.url}${pathWithSlash}${querySuffix}`
}

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text
 */
function truncateText(text, maxLength) {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}

/**
 * Generate meta description (140-160 characters)
 * @param {string} description - Description text
 * @returns {string} Optimized meta description
 */
export function generateMetaDescription(description) {
  if (!description) return SITE_CONFIG.description
  return truncateText(description, 160)
}

/**
 * Generate comprehensive meta tags for vue-meta
 * @param {Object} options - Meta tag options
 * @param {string} options.title - Page title
 * @param {string} options.description - Page description
 * @param {string} options.path - Current path
 * @param {string} options.image - Open Graph image URL
 * @param {string} options.type - Open Graph type (article, website, etc.)
 * @param {string} options.author - Article author
 * @param {Date|string} options.publishedTime - Article published time
 * @param {Date|string} options.modifiedTime - Article modified time
 * @param {Array<string>} options.keywords - Meta keywords
 * @returns {Object} vue-meta configuration object
 */
export function generateMetaTags({
  title,
  description,
  path,
  image,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  keywords = [],
}) {
  // Strip any existing site name suffix before appending (prevents duplication)
  const cleanTitle = title ? title.replace(/\s*\|\s*gamesnest\s*$/i, '').trim() : null
  const fullTitle = cleanTitle ? `${cleanTitle} | ${SITE_CONFIG.name}` : SITE_CONFIG.name
  const metaDescription = generateMetaDescription(description)
  const canonicalUrl = generateCanonicalUrl(path)
  const ogImage = image || `${SITE_CONFIG.url}/og-image.png`

  const meta = [
    // Basic meta tags
    { name: 'description', content: metaDescription },
    { name: 'robots', content: 'index, follow' },

    // Open Graph tags
    { property: 'og:title', content: title || SITE_CONFIG.name },
    { property: 'og:description', content: metaDescription },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:type', content: type },
    { property: 'og:image', content: ogImage },
    { property: 'og:site_name', content: SITE_CONFIG.name },
    { property: 'og:locale', content: SITE_CONFIG.locale },

    // Twitter Card tags
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: SITE_CONFIG.twitter },
    { name: 'twitter:title', content: title || SITE_CONFIG.name },
    { name: 'twitter:description', content: metaDescription },
    { name: 'twitter:image', content: ogImage },
  ]

  // Add keywords if provided
  if (keywords.length > 0) {
    meta.push({ name: 'keywords', content: keywords.join(', ') })
  }

  // Add article-specific meta tags
  if (type === 'article') {
    if (author) {
      meta.push({ property: 'article:author', content: author })
    }
    if (publishedTime) {
      meta.push({
        property: 'article:published_time',
        content: new Date(publishedTime).toISOString()
      })
    }
    if (modifiedTime) {
      meta.push({
        property: 'article:modified_time',
        content: new Date(modifiedTime).toISOString()
      })
    }
  }

  return {
    title: fullTitle,
    meta,
    link: [
      { rel: 'canonical', href: canonicalUrl }
    ],
  }
}

/**
 * Generate Article structured data (Schema.org)
 * @param {Object} article - Article data
 * @param {string} article.title - Article title
 * @param {string} article.description - Article description
 * @param {string} article.author - Article author
 * @param {Date|string} article.publishDate - Publish date
 * @param {Date|string} article.modifiedDate - Modified date
 * @param {string} article.image - Article image URL
 * @param {string} article.url - Article URL
 * @param {string} article.category - Article category
 * @returns {Object} Article schema JSON-LD
 */
export function generateArticleSchema(article) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: generateMetaDescription(article.description),
    image: article.image || `${SITE_CONFIG.url}/og-image.png`,
    author: {
      '@type': 'Person',
      name: article.author || 'GamesNest Editorial Team',
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_CONFIG.name,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_CONFIG.url}${SITE_CONFIG.logo}`,
      },
    },
    datePublished: new Date(article.publishDate).toISOString(),
    dateModified: article.modifiedDate
      ? new Date(article.modifiedDate).toISOString()
      : new Date(article.publishDate).toISOString(),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url || generateCanonicalUrl(article.path || ''),
    },
  }
}

/**
 * Generate BreadcrumbList structured data
 * @param {Array<Object>} breadcrumbs - Breadcrumb items
 * @param {string} breadcrumbs[].name - Breadcrumb name
 * @param {string} breadcrumbs[].path - Breadcrumb path
 * @returns {Object} BreadcrumbList schema JSON-LD
 */
export function generateBreadcrumbSchema(breadcrumbs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: generateCanonicalUrl(crumb.path),
    })),
  }
}

/**
 * Generate Organization structured data
 * @returns {Object} Organization schema JSON-LD
 */
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    logo: `${SITE_CONFIG.url}${SITE_CONFIG.logo}`,
    description: SITE_CONFIG.description,
    sameAs: [
      `https://twitter.com/${SITE_CONFIG.twitter.replace('@', '')}`,
    ],
  }
}

/**
 * Generate WebSite structured data with search action
 * @returns {Object} WebSite schema JSON-LD
 */
export function generateWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    description: SITE_CONFIG.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_CONFIG.url}/search/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

/**
 * Inject JSON-LD structured data into page
 * @param {Object|Array<Object>} schema - Schema.org structured data
 * @returns {Object} vue-meta script configuration
 */
export function injectStructuredData(schema) {
  const schemas = Array.isArray(schema) ? schema : [schema]

  return {
    script: schemas.map((s) => ({
      type: 'application/ld+json',
      json: s,
    })),
  }
}

/**
 * Generate complete vue-meta configuration for article pages
 * @param {Object} article - Article data
 * @param {Array<Object>} breadcrumbs - Breadcrumb items
 * @returns {Object} Complete vue-meta configuration
 */
export function generateArticleMetaInfo(article, breadcrumbs = []) {
  const metaTags = generateMetaTags({
    title: article.metaTitle || article.title,
    description: article.metaDescription || article.description,
    path: article.path || '',
    image: article.image,
    type: 'article',
    author: article.author,
    publishedTime: article.publishDate,
    modifiedTime: article.modifiedDate,
    keywords: article.metaKeywords ? article.metaKeywords.split(',').map(k => k.trim()) : [],
  })

  const schemas = [
    generateArticleSchema(article),
  ]

  if (breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(breadcrumbs))
  }

  const structuredData = injectStructuredData(schemas)

  return {
    ...metaTags,
    ...structuredData,
  }
}

/**
 * Generate complete vue-meta configuration for category/list pages
 * @param {Object} options - Page options
 * @param {string} options.title - Page title
 * @param {string} options.description - Page description
 * @param {string} options.path - Current path
 * @param {Array<Object>} options.breadcrumbs - Breadcrumb items
 * @returns {Object} Complete vue-meta configuration
 */
export function generateListPageMetaInfo({ title, description, path, breadcrumbs = [] }) {
  const metaTags = generateMetaTags({
    title,
    description,
    path,
    type: 'website',
  })

  const schemas = []

  if (breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(breadcrumbs))
  }

  const structuredData = schemas.length > 0 ? injectStructuredData(schemas) : {}

  return {
    ...metaTags,
    ...structuredData,
  }
}

/**
 * Generate complete vue-meta configuration for homepage
 * @returns {Object} Complete vue-meta configuration
 */
export function generateHomePageMetaInfo() {
  const metaTags = generateMetaTags({
    title: null, // Will use site name only
    description: SITE_CONFIG.description,
    path: '/',
    type: 'website',
  })

  const structuredData = injectStructuredData([
    generateOrganizationSchema(),
    generateWebSiteSchema(),
  ])

  return {
    ...metaTags,
    ...structuredData,
  }
}

/**
 * Helper to build breadcrumbs from route
 * @param {Object} route - Vue Router route object
 * @param {Object} additionalCrumbs - Additional breadcrumb data
 * @returns {Array<Object>} Breadcrumb items
 */
export function buildBreadcrumbs(route, additionalCrumbs = {}) {
  const breadcrumbs = [
    { name: 'Home', path: '/' }
  ]

  const pathParts = route.path.split('/').filter(Boolean)

  if (pathParts.length > 0) {
    const category = pathParts[0]
    const categoryNames = {
      news: 'News',
      reviews: 'Reviews',
      guides: 'Guides',
      deals: 'Deals',
      games: 'Games',
      platforms: 'Platforms',
    }

    breadcrumbs.push({
      name: categoryNames[category] || category,
      path: `/${category}/`,
    })

    // Add subcategory if exists
    if (pathParts.length > 1 && additionalCrumbs.subcategory) {
      breadcrumbs.push({
        name: additionalCrumbs.subcategory,
        path: `/${category}/${pathParts[1]}/`,
      })
    }

    // Add current page if exists
    if (additionalCrumbs.current) {
      breadcrumbs.push({
        name: additionalCrumbs.current,
        path: route.path,
      })
    }
  }

  return breadcrumbs
}

export default {
  generateCanonicalUrl,
  generateMetaDescription,
  generateMetaTags,
  generateArticleSchema,
  generateBreadcrumbSchema,
  generateOrganizationSchema,
  generateWebSiteSchema,
  injectStructuredData,
  generateArticleMetaInfo,
  generateListPageMetaInfo,
  generateHomePageMetaInfo,
  buildBreadcrumbs,
  SITE_CONFIG,
}
